import {
    MessageProps,
    MessagesViewProps,
} from './types'
import React, {useContext} from 'react'

import DateDivider from './DateDivider'
import {EmbedContext} from 'components/EmbedContext'

import Message from './Message'
import {isSameDay} from 'utils/datetime'
import {styled} from 'stitches/config'
import {styledMessagesView} from './styles'
import {useAppSelector} from '../../hooks'
import {useTranslation} from 'react-i18next'

const StyledMessagesView = styled('div', styledMessagesView)

const isRepeated = (
    current: MessageProps,
    previous: MessageProps,
    showHidden: boolean,
): boolean => current.message_user_id === previous.message_user_id &&
    !(previous.message_hidden && !showHidden) &&
    !previous.message_deleted &&
    !current.message_deleted &&
    Math.abs(current.message_time - previous.message_time) < 600

const MessagesView = ({
    currentQueue,
    channelId,
    isChannel,
    messages,
    bottomElementSetRef,
    onAction,
    onDeleteMessage,
    onRemovePendingMessage,
    onRetryMessage,
    topElementSetRef,
}: MessagesViewProps): React.ReactElement => {
    const {t} = useTranslation()

    const sessionUserId = useAppSelector(({ninchat}) => ninchat.user_id)
    const isAdmin = useAppSelector(({ninchat}) => !!ninchat.user_attrs.admin)
    const isAudience = useAppSelector(({ninchat}) => !!ninchat?.user_channels?.[channelId || '']?.channel_attrs.audience_id)
    const isModerator = isChannel && useAppSelector(({ninchat}) => ninchat?.user_channels?.[channelId || '']?.channel_members?.[ninchat.user_id || '']?.member_attrs ?? {}).moderator

    const showHiddenMessages = useAppSelector(({ninchat}) => !!ninchat.user_settings.show_hidden_messages)
    const channelMembers = useAppSelector(({ninchat}) => ninchat.user_channels[channelId || ''])?.channel_members || {}

    const embed = useContext(EmbedContext)
    const isEmbedQueueChannel = embed?.variant === 'queue' || embed?.variant === 'channel'

    // index of the last message that is actually rendered (hidden-and-unshown messages are skipped)
    const lastVisibleIndex = (messages ?? []).reduce((last, currentMessage, index) => {
        const isVisible = !(currentMessage.message_hidden && !showHiddenMessages)

        return isVisible ? index : last
    }, -1)

    return (
        <StyledMessagesView aria-label={t('Chat messages')} role='log'>
            {
                messages?.map((message: MessageProps, i: number, _messages: MessageProps[]) => {
                    const previousMessage = _messages[i - 1] || null
                    const me = sessionUserId === message.message_user_id
                    const isLast = i === lastVisibleIndex

                    const ref = i === 0 ? topElementSetRef : i === messages.length - 1 ? bottomElementSetRef : undefined

                    const hasValidTime = !!message?.message_time
                    const showDateDivider = !isEmbedQueueChannel && (!previousMessage?.message_time || !isSameDay(message?.message_time, previousMessage?.message_time))
                    // When a date divider is shown, the message should not be treated as repeated
                    const repeated = !showDateDivider && previousMessage && isRepeated(message, previousMessage, showHiddenMessages)

                    return (
                        <React.Fragment key={message.message_id}>
                            {showDateDivider && hasValidTime ? <DateDivider timestamp={message.message_time} /> : null}
                            <div ref={ref}>
                                <Message
                                    channelId={channelId}
                                    currentQueue={currentQueue}
                                    isAdmin={isAdmin}
                                    isAudienceChannel={isAudience}
                                    isChannel={isChannel}
                                    isLast={isLast}
                                    isMe={me}
                                    isModerator={isModerator}
                                    isRepeated={repeated}
                                    memberAttrs={channelMembers?.[message.message_user_id || '']?.member_attrs}
                                    message={message}
                                    onAction={onAction}
                                    onDeleteMessage={onDeleteMessage}
                                    onRemovePendingMessage={onRemovePendingMessage}
                                    onRetryMessage={onRetryMessage}
                                    showHidden={showHiddenMessages}
                                    timeOnly={true}
                                />
                            </div>
                        </React.Fragment>
                    )
                })
            }
        </StyledMessagesView>
    )
}

export default MessagesView
